import LoadingFullScreen from "components/common/LoadingFullScreen";
import { useGetAllClustersQuery } from "generated/graphql";
import { RedirectToCluster } from "in_queue/RedirectToCluster";
import {
    Cluster,
    ClusterStatus,
    convertHasuraClusterToCluster,
    isSameCluster
} from "in_queue/types/clusterType";
import {
    getRegionConfig,
    getStageConfig,
    InQueueRegionConfig,
    StageConfig
} from "in_queue/types/configPerRegion";
import { InQueueSubscription } from "in_queue/types/projectType";
import { createContext, PropsWithChildren, useContext, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useSubscriptions } from "./InQueueSubscriptionsContext";

interface ClusterValue {
    cluster: Cluster;
    clusters: Cluster[];
    regionConfig: InQueueRegionConfig;
    stageConfig: StageConfig;
}

export const ClusterContext = createContext<ClusterValue | null>(null);

export const ClusterProvider: React.FC<PropsWithChildren> = ({ children }) => {
    const { clusterSlug } = useParams();
    if (!clusterSlug) {
        throw new Error("Cluster not found in the URL");
    }

    const { data: allClusters, loading: allClustersLoading } =
        useGetAllClustersQuery();

    const clusters = convertHasuraClusterToCluster(allClusters);
    let contextVal: ClusterValue | null = null;
    if (allClustersLoading) {
        // Don't load children until we have a cluster.
        // Future: It would be better to delegate loading state to the children.
        return <LoadingFullScreen />;
    }
    if (!clusters.length) {
        // Rather than 404, redirect to a valid cluster.
        return <RedirectToCluster />;
    }

    const cluster = clusters.find(
        (cluster: Cluster) => cluster.slug === clusterSlug
    );
    if (!cluster) {
        return <RedirectToCluster />;
    }

    const regionConfig = getRegionConfig(cluster.region);
    const stageConfig = getStageConfig(cluster);
    contextVal = {
        cluster,
        clusters,
        regionConfig,
        stageConfig
    } as ClusterValue;
    return (
        <ClusterContext.Provider value={contextVal}>
            {children}
        </ClusterContext.Provider>
    );
};

const useClusterValue = (): ClusterValue => {
    const context = useContext(ClusterContext);
    if (!context) {
        throw new Error(
            "useClusterValue must be used within a ClusterProvider"
        );
    }
    return context;
};

export const useCluster = (): Cluster => {
    const value = useClusterValue();
    return value.cluster;
};

export const useClusters = (): Cluster[] => {
    const value = useClusterValue();
    return value.clusters;
};

export const useClustersWithSubscriptions = (): Cluster[] => {
    const clusters = useClusters();
    const subscriptions = useSubscriptions();

    return useMemo(() => {
        if (subscriptions === "loading") {
            return [];
        }

        return clusters.filter((cluster: Cluster) =>
            subscriptions.some((subscription: InQueueSubscription) =>
                isSameCluster(subscription.clusterInfo, cluster)
            )
        );
    }, [clusters, subscriptions]);
};

const isActiveCluster = (cluster: Cluster): boolean =>
    ![
        ClusterStatus.Hidden,
        ClusterStatus.AllDisabled,
        ClusterStatus.AfterDp
    ].includes(cluster.status);

export const useActiveClustersWithSubscriptions = (): Cluster[] => {
    const clusters = useClustersWithSubscriptions();
    return clusters.filter(isActiveCluster);
};

export const usePastClustersWithSubscriptions = (): Cluster[] => {
    const clusters = useClustersWithSubscriptions();
    return clusters.filter(
        (cluster: Cluster) => cluster.status === ClusterStatus.AfterDp
    );
};

export const useClustersWithoutSubscriptions = (): Cluster[] => {
    const clusters = useClusters();
    const subscriptions = useSubscriptions();

    if (subscriptions === "loading") {
        return [];
    }

    return clusters.filter(
        (cluster: Cluster) =>
            !subscriptions.some((subscription: InQueueSubscription) =>
                isSameCluster(subscription.clusterInfo, cluster)
            )
    );
};

export const useActiveClustersWithoutSubscriptions = (): Cluster[] => {
    const clusters = useClustersWithoutSubscriptions();
    return clusters.filter(isActiveCluster);
};

export const useRegionConfig = (): InQueueRegionConfig => {
    const value = useClusterValue();
    return value.regionConfig;
};

export const useStageConfig = (): StageConfig => {
    const value = useClusterValue();
    return value.stageConfig;
};
